/* =====================================================================
 * UsageChart — run counts or token usage per day / week / month.
 *
 * Form: stacked bars. The question is "how much usage, split by which
 * agent" — a magnitude over discrete time buckets, which is bars, not a
 * line. Stacked (rather than grouped) because the total matters as much
 * as the split.
 *
 * Colour: categorical, two slots in fixed order, assigned by workspace
 * identity — filtering never repaints the survivors. Values are drawn
 * in text tokens, never the series colour.
 *
 * One axis. Never two.
 * ===================================================================== */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import type { DashboardSeries } from '@auw/api-client';

import { CATEGORICAL, type ChartMode } from './chartPalette';
import {
  formatDashboardTokenCount,
  formatUsageTick,
  selectUsagePointMetric,
  USAGE_BAR_WIDTH,
  usageColumnWidth,
  type UsageMetric,
} from './dashboardMetrics';
import { EmptyState, ScrollX } from './dashboard.styles';

const PLOT_H = 168;
/* Sized so the default 30-day view fits the 1080px page column without
 * scrolling — a chart whose *default* state needs a horizontal drag on
 * desktop is a papercut. Narrower windows still scroll (ScrollX). */
const BAR_W = USAGE_BAR_WIDTH;
const BAR_GAP = 8;

/* Tick labels ("7/1") are wider than a 22px column and overflow it on
 * both sides. Without side padding the first one is clipped by the
 * scroller's left edge — the inline padding is the label's overhang,
 * not decoration. */
const TICK_OVERHANG = 14;

const Plot = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${BAR_GAP}px;
  height: ${PLOT_H}px;
  padding: 20px ${TICK_OVERHANG}px 0;
  position: relative;
`;

/* Recessive baseline — the only rule the plot needs. A full grid would
 * compete with the bars for attention. */
const Baseline = styled.div`
  position: absolute;
  left: ${TICK_OVERHANG}px;
  right: ${TICK_OVERHANG}px;
  bottom: 0;
  height: 1px;
  background: ${({ theme }) => theme.colors.borderLight};
`;

/* 칸 폭은 눈금 길이에 맞추고(월별이 넓다), 막대는 칸 가운데 같은 굵기로 둔다. */
const Column = styled.div<{ $w: number }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  width: ${({ $w }) => $w}px;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.accent};
    outline-offset: 3px;
    border-radius: 4px;
  }
`;

const Stack = styled.div`
  width: ${BAR_W}px;
  display: flex;
  flex-direction: column-reverse;
  justify-content: flex-start;
  gap: 2px; /* surface gap between stacked segments */
  height: ${PLOT_H}px;
  justify-self: end;
  align-self: flex-end;
`;

/* The stack's *outer* ends get the radius: the top segment rounds up,
 * the bottom one rounds down. Interior joins stay square so the 2px
 * surface gap reads as a division rather than a pill boundary.
 *
 * A single-segment bar is both top and bottom, so it rounds on all four
 * corners — which is the common case in this chart. */
const Segment = styled.div<{
  $color: string;
  $h: number;
  $top: boolean;
  $bottom: boolean;
}>`
  width: 100%;
  height: ${({ $h }) => Math.max($h, 3)}px;
  background: ${({ $color }) => $color};
  border-radius: ${({ $top, $bottom }) =>
    [
      $top ? '5px' : '0',
      $top ? '5px' : '0',
      $bottom ? '5px' : '0',
      $bottom ? '5px' : '0',
    ].join(' ')};
`;

const Value = styled.span`
  font-size: 0.6875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-variant-numeric: tabular-nums;
`;

const Tick = styled.span`
  font-size: 0.6875rem;
  color: ${({ theme }) => theme.colors.textMuted};
  white-space: nowrap;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 14px;
`;

const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Dot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 2px;
  background: ${({ $color }) => $color};
  flex-shrink: 0;
`;

export interface UsageChartProps {
  data: DashboardSeries;
  metric: UsageMetric;
  mode: ChartMode;
  /** Human label per workspace id; falls back to the raw id. */
  labelFor: (workspaceType: string) => string;
}

export const UsageChart: React.FC<UsageChartProps> = ({
  data,
  metric,
  mode,
  labelFor,
}) => {
  const { t, i18n } = useTranslation();
  const [hovered, setHovered] = useState<string | null>(null);

  const max = useMemo(
    () => Math.max(
      1,
      ...data.points.map((point) => selectUsagePointMetric(point, metric).total),
    ),
    [data.points, metric],
  );

  const formatTick = (bucket: string) =>
    formatUsageTick(bucket, data.granularity, i18n.language);

  const columnWidth = usageColumnWidth(data.granularity);

  const hasMetricData = data.points.some(
    (point) => selectUsagePointMetric(point, metric).hasData,
  );

  if (!data.points.length) {
    return <EmptyState>{t('common:dashboard.noUsageYet')}</EmptyState>;
  }
  if (!hasMetricData) {
    return <EmptyState>{t('common:dashboard.tokenDataUnavailable')}</EmptyState>;
  }

  return (
    <>
      <ScrollX>
        <Plot>
          <Baseline aria-hidden />
          {data.points.map((point) => {
            const selected = selectUsagePointMetric(point, metric);
            const pointTotal = selected.total;
            const pointByWorkspace = selected.byWorkspace;
            const scale = (pointTotal / max) * (PLOT_H - 24);
            const exactValue = selected.hasData
              ? new Intl.NumberFormat(i18n.language).format(pointTotal)
              : '—';
            const columnLabel = `${formatTick(point.bucket)} · ${t(
              metric === 'tokens'
                ? 'common:dashboard.tokensCount'
                : 'common:dashboard.runsCount',
              { count: exactValue },
            )}`;
            return (
              <Column
                key={point.bucket}
                $w={columnWidth}
                role="img"
                tabIndex={0}
                aria-label={columnLabel}
                onMouseEnter={() => setHovered(point.bucket)}
                onMouseLeave={() => setHovered(null)}
                onFocus={() => setHovered(point.bucket)}
                onBlur={() => setHovered(null)}
                title={columnLabel}
              >
                <Stack>
                  {data.workspaces.map((workspace, index) => {
                    const count = pointByWorkspace[workspace] ?? 0;
                    if (!count) return null;
                    const height = (count / Math.max(pointTotal, 1)) * scale;
                    const isTop =
                      data.workspaces
                        .slice(index + 1)
                        .every((later) => !pointByWorkspace[later]);
                    const isBottom =
                      data.workspaces
                        .slice(0, index)
                        .every((earlier) => !pointByWorkspace[earlier]);
                    return (
                      <Segment
                        key={workspace}
                        $color={CATEGORICAL[mode][index % CATEGORICAL[mode].length]}
                        $h={height}
                        $top={isTop}
                        $bottom={isBottom}
                      />
                    );
                  })}
                </Stack>
                {/* Direct label only on the hovered column — a number on
                    every bar turns the plot into a table. */}
                <Value>
                  {hovered === point.bucket
                    ? metric === 'tokens'
                      ? formatDashboardTokenCount(pointTotal)
                      : pointTotal
                    : ''}
                </Value>
                <Tick>
                  {formatTick(point.bucket)}
                </Tick>
              </Column>
            );
          })}
        </Plot>
      </ScrollX>

      {/* Legend is always present for ≥2 series — identity must never be
          carried by colour alone. */}
      {data.workspaces.length > 1 && (
        <Legend>
          {data.workspaces.map((workspace, index) => (
            <LegendItem key={workspace}>
              <Dot $color={CATEGORICAL[mode][index % CATEGORICAL[mode].length]} />
              {labelFor(workspace)}
            </LegendItem>
          ))}
        </Legend>
      )}
    </>
  );
};
